import * as React from "react";
import {useIntl} from "react-intl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowUp, faArrowDown, faTrash} from "@fortawesome/pro-regular-svg-icons";

interface Position {
    top: number;
    left: number;
    width: number;
    height: number;
}

interface BlockOverlayControlsProps {
    blockId: string;
    blockType: string;
    position: Position;
    isFirst: boolean;
    isLast: boolean;
    isHovered: boolean;
    isSelected: boolean;
    onHover: () => void;
    onLeave: () => void;
    onSelect: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onDelete: () => void;
}

const BlockOverlayControls: React.FC<BlockOverlayControlsProps> = ({
    blockId,
    blockType,
    position,
    isFirst,
    isLast,
    isHovered,
    isSelected,
    onHover,
    onLeave,
    onSelect,
    onMoveUp,
    onMoveDown,
    onDelete,
}) => {
    const intl = useIntl();

    const handleMoveUp = (e: React.MouseEvent) => {
        e.stopPropagation();
        onMoveUp();
    };

    const handleMoveDown = (e: React.MouseEvent) => {
        e.stopPropagation();
        onMoveDown();
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete();
    };

    return (
        <div
            className={`absolute transition-all duration-150 cursor-pointer ${
                isHovered || isSelected
                    ? "ring-2 ring-red-500 ring-offset-1"
                    : "hover:ring-2 hover:ring-red-300 hover:ring-offset-1"
            }`}
            style={{
                top: position.top,
                left: position.left,
                width: position.width,
                height: position.height,
                pointerEvents: "auto",
            }}
            onMouseEnter={onHover}
            onMouseLeave={onLeave}
            onClick={onSelect}
        >
            {/* Controls toolbar - visible on hover */}
            {(isHovered || isSelected) && (
                <div
                    className="absolute -top-8 right-0 flex items-center gap-1 bg-white rounded-t-md shadow-md px-1 py-0.5 border border-gray-200"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Move up */}
                    <button
                        type="button"
                        onClick={handleMoveUp}
                        disabled={isFirst}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                        title={intl.formatMessage({id: "emailBlock.action.moveUp"})}
                    >
                        <FontAwesomeIcon icon={faArrowUp} className="w-3 h-3" />
                    </button>

                    {/* Move down */}
                    <button
                        type="button"
                        onClick={handleMoveDown}
                        disabled={isLast}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                        title={intl.formatMessage({id: "emailBlock.action.moveDown"})}
                    >
                        <FontAwesomeIcon icon={faArrowDown} className="w-3 h-3" />
                    </button>

                    <div className="w-px h-4 bg-gray-200" />

                    {/* Delete */}
                    <button
                        type="button"
                        onClick={handleDelete}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-sm"
                        title={intl.formatMessage({id: "emailBlock.action.delete"})}
                    >
                        <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default BlockOverlayControls;
